import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { T } from '@tolgee/react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { MutatingDots } from 'react-loader-spinner';
import {
	NotificationInterface,
	OrderInterface,
} from '../../components/OrdersTable/interfaces/Order.interface';
import { OrderTarget } from '../../components/OrdersTable/interfaces/OrderStatus.interface';
import { OrdersService } from '../../services/orders.services';
import Emitter, { EventType } from '../../services/events';
import { OrderContext } from '../../context/OrderContext/order.context';
import TrackingMap from '../Public/OrderTracking/components/TrackingMap';
import EtaCard from '../Public/OrderTracking/components/EtaCard';
import RouteLegend from '../Public/OrderTracking/components/RouteLegend';
import TruckSwitcher from '../Public/OrderTracking/components/TruckSwitcher';
import useTrackingOrder, {
	routeModeFor,
} from '../../components/OrderRouteMap/useTrackingOrder';
import {
	driverNotice,
	isTruckAssigned,
} from '../Public/OrderTracking/orderTracking.utils';
import '../Public/OrderTracking/order-tracking.css';
import TrackingProgress from './components/TrackingProgress';

type OrderPageStatus = 'loading' | 'ready' | 'error';

export interface OrderPageDetailsProps {
	order: OrderInterface;
	isOpen: boolean;
	onBack: () => void;
}

interface OrderPageProps {
	target: OrderTarget;
	renderDetails: (props: OrderPageDetailsProps) => React.ReactNode;
}

const OrderPage: React.FC<OrderPageProps> = ({ target, renderDetails }) => {
	const { orderId } = useParams<{ orderId: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { setOrderId } = useContext(OrderContext);
	const [status, setStatus] = useState<OrderPageStatus>('loading');
	const [order, setOrder] = useState<OrderInterface>();
	const [selectedTruckId, setSelectedTruckId] = useState<number>();
	const mode = routeModeFor(target);
	const inFlightRef = useRef<boolean>(false);

	const id = Number(orderId);

	const load = useCallback(
		async (silent = false) => {
			if (!Number.isInteger(id) || id <= 0) {
				setStatus('error');
				return;
			}
			if (silent && inFlightRef.current) return;
			inFlightRef.current = true;
			if (!silent) setStatus('loading');
			try {
				const { data } = await OrdersService.getOrder(id, target);
				setOrder(data as unknown as OrderInterface);
				setOrderId(id);
				setStatus('ready');
			} catch (e) {
				if (!silent) setStatus('error');
			} finally {
				inFlightRef.current = false;
			}
		},
		[id, setOrderId, target],
	);

	useEffect(() => {
		setOrder(undefined);
		setSelectedTruckId(undefined);
		load().then(() => {});
	}, [load]);

	useEffect(() => {
		const isCurrent = (notify?: NotificationInterface) =>
			!!notify?.groupId && parseInt(notify.groupId, 10) === id;

		const onOrderNotify = (notify: NotificationInterface) => {
			if (isCurrent(notify)) load(true).then(() => {});
		};

		Emitter.on(EventType.ORDER_LIST_REFRESH, onOrderNotify);
		return () => {
			Emitter.off(EventType.ORDER_LIST_REFRESH, onOrderNotify);
		};
	}, [id, load]);

	const tracking = useTrackingOrder(order, {
		mode,
		showPendingTrucks: target === OrderTarget.transporter,
	});

	const singleTruckId =
		tracking?.deliveryMode === 'single' && tracking.trucks.length === 1
			? tracking.trucks[0].id
			: undefined;

	useEffect(() => {
		if (singleTruckId !== undefined) setSelectedTruckId(singleTruckId);
	}, [singleTruckId]);

	const toggleTruck = (truckId: number) => {
		// A single-truck order keeps its truck selected; there is nothing to switch.
		if (singleTruckId !== undefined) return;
		const truck = tracking?.trucks.find((item) => item.id === truckId);
		if (truck && !isTruckAssigned(truck)) return;
		setSelectedTruckId((current) =>
			current === truckId ? undefined : truckId,
		);
	};

	const goBack = () => {
		if (location.key !== 'default') {
			navigate(-1);
		} else {
			navigate('/orders');
		}
	};

	const selectedTruck = tracking?.trucks.find(
		(truck) => truck.id === selectedTruckId,
	);
	const notice =
		tracking && selectedTruck
			? driverNotice(tracking, selectedTruck, mode)
			: undefined;
	const cardNotice = notice && !notice.onMarker ? notice.key : undefined;

	return (
		<div className="flex gap-5 w-[calc(100vw-67px)] xl-2xl:w-[calc(100vw-83px)] h-full pl-5 pr-7.5">
			<main className="relative flex-1 min-w-0 h-full border border-solid border-gray-10 rounded-2xl overflow-hidden bg-track-map">
				{status === 'ready' && tracking ? (
					<>
						<TrackingMap
							order={tracking}
							mode={mode}
							selectedTruckId={selectedTruckId}
							onSelectTruck={toggleTruck}
						/>
						<div className="absolute left-5 top-5 z-[600] flex flex-col items-start gap-3">
							{selectedTruck ? (
								<EtaCard
									truck={selectedTruck}
									distanceKm={tracking.distanceKm}
									updatedAt={tracking.updatedAt}
									notice={cardNotice}
									className="relative left-auto top-auto lg:top-auto max-w-none"
								/>
							) : null}
							<TrackingProgress order={tracking} className="relative" />
						</div>
						<div className="absolute left-5 bottom-6 z-[600] flex flex-col items-stretch gap-3">
							<TruckSwitcher
								trucks={tracking.trucks}
								selectedTruckId={selectedTruckId}
								onSelect={setSelectedTruckId}
							/>
							<RouteLegend multipleTrucks={tracking.trucks.length > 1} />
						</div>
					</>
				) : null}

				{status === 'loading' ? (
					<div className="absolute inset-0 flex items-center justify-center">
						<MutatingDots
							height="100"
							width="100"
							color="#7693F4"
							secondaryColor="#494C83"
							radius="12.5"
							ariaLabel="mutating-dots-loading"
							visible
						/>
					</div>
				) : null}

				{status === 'error' ? (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
						<SypacText variant="body-regular-large">
							<p className="text-gray-80 text-lg">
								<T keyName="orderTracking.errorTitle">
									Couldn't load this order
								</T>
							</p>
						</SypacText>
						<div className="flex gap-3">
							<SypacButton variant="secondary" size="small">
								<button
									type="button"
									className="px-5"
									onClick={() => load().then(() => {})}
								>
									<T keyName="orderTracking.tryAgain">Try again</T>
								</button>
							</SypacButton>
							<SypacButton variant="secondary" size="small">
								<button type="button" className="px-5" onClick={goBack}>
									<T keyName="clientSelectCategory.backButton">Back</T>
								</button>
							</SypacButton>
						</div>
					</div>
				) : null}
			</main>

			<div className="flex-none w-[716px] h-full relative">
				{order
					? renderDetails({
							order,
							isOpen: status === 'ready',
							onBack: goBack,
					  })
					: null}
			</div>
		</div>
	);
};

export default OrderPage;
